import React, { useEffect, useMemo, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';

interface OnlinePlayer {
  id: string;
  name: string;
  level: number;
  pieces: number;
  position: { x: number; y: number };
  hp: string;
  shipType: string;
}

interface ModeratorPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ModeratorPanelState {
  isBusy: boolean;
  message: string | null;
  messageType: 'success' | 'error' | 'info';
}

export const ModeratorPanel: React.FC<ModeratorPanelProps> = ({ socket, isOpen, onClose }) => {
  const { confirm } = useConfirm();
  const [panelState, setPanelState] = useState<ModeratorPanelState>({
    isBusy: false,
    message: null,
    messageType: 'info',
  });
  const [players, setPlayers] = useState<OnlinePlayer[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [kickReason, setKickReason] = useState<string>('');
  const [announceMessage, setAnnounceMessage] = useState<string>('');

  const selectedPlayer = useMemo(() => {
    return players.find((p) => p.id === selectedPlayerId) || null;
  }, [players, selectedPlayerId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const call = <T,>(
    event: string,
    data: unknown,
    onAck: (result: T) => void
  ): void => {
    if (!socket) {
      setPanelState({ isBusy: false, message: 'Socket not connected', messageType: 'error' });
      return;
    }
    setPanelState((prev) => ({ ...prev, isBusy: true, message: null }));
    socket.emit(event, data, (result: T) => {
      setPanelState((prev) => ({ ...prev, isBusy: false }));
      onAck(result);
    });
  };

  const refreshPlayers = (): void => {
    call<{ success: boolean; message?: string; players?: OnlinePlayer[] }>('mod:playersOnline', {}, (result) => {
      if (!result.success) {
        setPanelState({ isBusy: false, message: result.message || 'Failed to load players', messageType: 'error' });
        return;
      }
      setPlayers(result.players || []);
      setPanelState({ isBusy: false, message: `Loaded ${(result.players || []).length} players`, messageType: 'success' });
    });
  };

  const teleportToSelected = (): void => {
    if (!selectedPlayerId) {
      setPanelState({ isBusy: false, message: 'Select a player first', messageType: 'error' });
      return;
    }
    call<{ success: boolean; message: string }>('mod:teleportToPlayer', { targetPlayerId: selectedPlayerId }, (result) => {
      setPanelState({ isBusy: false, message: result.message, messageType: result.success ? 'success' : 'error' });
    });
  };

  const kickSelected = (): void => {
    if (!selectedPlayerId) {
      setPanelState({ isBusy: false, message: 'Select a player first', messageType: 'error' });
      return;
    }
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Kick Player',
        message: 'Confirm kick?',
        confirmText: 'Kick',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;

      call<{ success: boolean; message: string }>(
        'mod:kick',
        { playerId: selectedPlayerId, reason: kickReason },
        (result) => {
          setPanelState({ isBusy: false, message: result.message, messageType: result.success ? 'success' : 'error' });
          if (result.success) {
            setSelectedPlayerId('');
            setKickReason('');
            refreshPlayers();
          }
        }
      );
    })();
  };

  const announce = (): void => {
    const msg = announceMessage.trim();
    if (!msg) {
      setPanelState({ isBusy: false, message: 'Message is required', messageType: 'error' });
      return;
    }
    call<{ success: boolean; message: string }>('mod:announce', { message: msg, type: 'info', duration: 6000 }, (result) => {
      setPanelState({ isBusy: false, message: result.message, messageType: result.success ? 'success' : 'error' });
      if (result.success) {
        setAnnounceMessage('');
      }
    });
  };

  return (
    <div
      className="font-mono"
      style={{
        position: 'fixed',
        right: 16,
        top: 16,
        width: 320,
        maxHeight: '85vh',
        overflow: 'hidden',
        backgroundColor: 'rgba(13, 17, 23, 0.96)',
        border: '2px solid #30363d',
        zIndex: 9999,
        fontFamily: 'var(--font-mono), monospace',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderBottom: '1px solid #30363d',
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: '#e6edf3' }}>MODERATOR</div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: 28,
            height: 28,
            backgroundColor: '#0d1117',
            border: '1px solid #30363d',
            color: '#e6edf3',
            cursor: 'pointer',
            borderRadius: 0,
          }}
        >
          X
        </button>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {panelState.message && (
          <div
            style={{
              padding: '8px 10px',
              fontSize: 11,
              border: '1px solid #30363d',
              backgroundColor:
                panelState.messageType === 'success'
                  ? 'rgba(34, 197, 94, 0.08)'
                  : panelState.messageType === 'error'
                  ? 'rgba(239, 68, 68, 0.08)'
                  : 'rgba(88, 166, 255, 0.08)',
              color:
                panelState.messageType === 'success'
                  ? '#22c55e'
                  : panelState.messageType === 'error'
                  ? '#ef4444'
                  : '#58a6ff',
            }}
          >
            {panelState.message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            disabled={panelState.isBusy}
            onClick={refreshPlayers}
            style={{
              flex: 1,
              padding: '8px 10px',
              backgroundColor: '#21262d',
              border: '1px solid #30363d',
              color: '#e6edf3',
              cursor: panelState.isBusy ? 'default' : 'pointer',
              borderRadius: 0,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Refresh Players
          </button>
          <button
            type="button"
            disabled={panelState.isBusy || !selectedPlayerId}
            onClick={teleportToSelected}
            style={{
              flex: 1,
              padding: '8px 10px',
              backgroundColor: '#1f6feb',
              border: '1px solid #388bfd',
              color: '#ffffff',
              cursor: panelState.isBusy || !selectedPlayerId ? 'default' : 'pointer',
              borderRadius: 0,
              fontSize: 11,
              fontWeight: 600,
              opacity: panelState.isBusy || !selectedPlayerId ? 0.6 : 1,
            }}
          >
            Teleport
          </button>
        </div>

        <div style={{ border: '1px solid #30363d' }}>
          <div
            style={{
              padding: '8px 10px',
              fontSize: 10,
              color: '#8b949e',
              borderBottom: '1px solid #30363d',
            }}
          >
            Online Players
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {players.length === 0 ? (
              <div style={{ padding: '10px 10px', fontSize: 11, color: '#8b949e' }}>No players loaded</div>
            ) : (
              players.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPlayerId(p.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    backgroundColor: selectedPlayerId === p.id ? '#161b22' : '#0d1117',
                    border: 'none',
                    borderBottom: '1px solid #21262d',
                    color: '#e6edf3',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono), monospace',
                    fontSize: 11,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{p.name}</span>
                    <span style={{ color: '#8b949e' }}>Lv{p.level}</span>
                  </div>
                  <div style={{ marginTop: 2, color: '#8b949e', fontSize: 10 }}>
                    {p.shipType} | {p.hp} | {p.pieces.toLocaleString()}₱
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, color: '#8b949e' }}>Selected</div>
          <div style={{ fontSize: 11, color: '#e6edf3' }}>
            {selectedPlayer ? selectedPlayer.name : 'None'}
          </div>

          <input
            type="text"
            value={kickReason}
            onChange={(e) => setKickReason(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Kick reason"
            style={{
              width: '100%',
              padding: '8px 10px',
              backgroundColor: '#0d1117',
              border: '1px solid #30363d',
              color: '#e6edf3',
              fontSize: 11,
              borderRadius: 0,
              outline: 'none',
            }}
          />

          <button
            type="button"
            disabled={panelState.isBusy || !selectedPlayerId}
            onClick={kickSelected}
            style={{
              width: '100%',
              padding: '8px 10px',
              backgroundColor: '#da3633',
              border: '1px solid #f85149',
              color: '#ffffff',
              cursor: panelState.isBusy || !selectedPlayerId ? 'default' : 'pointer',
              opacity: panelState.isBusy || !selectedPlayerId ? 0.6 : 1,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 0,
            }}
          >
            Kick
          </button>
        </div>

        <div style={{ borderTop: '1px solid #30363d', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, color: '#8b949e' }}>Announcement</div>
          <textarea
            value={announceMessage}
            onChange={(e) => setAnnounceMessage(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Message"
            rows={3}
            style={{
              width: '100%',
              padding: '8px 10px',
              backgroundColor: '#0d1117',
              border: '1px solid #30363d',
              color: '#e6edf3',
              fontSize: 11,
              borderRadius: 0,
              outline: 'none',
              resize: 'none',
            }}
          />
          <button
            type="button"
            disabled={panelState.isBusy || announceMessage.trim().length === 0}
            onClick={announce}
            style={{
              width: '100%',
              padding: '8px 10px',
              backgroundColor: '#238636',
              border: '1px solid #2ea043',
              color: '#ffffff',
              cursor: panelState.isBusy || announceMessage.trim().length === 0 ? 'default' : 'pointer',
              opacity: panelState.isBusy || announceMessage.trim().length === 0 ? 0.6 : 1,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 0,
            }}
          >
            Broadcast
          </button>
        </div>
      </div>
    </div>
  );
};
